<script setup>
import {useRoute} from "vue2-helpers/vue-router";
import {computed, onMounted, provide, ref, watch} from "vue";
import projectsService from "@/services/projects.service";
import EditProject from "@/components/trackers/TrackerProjectEdit.vue";
import AddProjectTask from "@/components/tasks/AddProjectTask.vue";
import useTrigger from "@/composables/useTrigger";
//eslint-disable-next-line
import Teleport from "vue2-teleport";
import TopBar from "@/views/trackers/tracker/TopBar.vue";
import SideBar from "@/views/trackers/tracker/SideBar.vue";
import TasksGrid from "@/views/projects/TasksGrid.vue";
import TasksBoard from "@/views/projects/TasksBoard.vue";
import ProjectTaskView from "@/components/tasks/viewProjectTask/ProjectTaskView.vue";
import Filters from "@/views/projects/Filters.vue";
import {trackerTypes} from "@/constants/trackers";
import useStoreTrigger from "@/composables/useStoreTrigger";
import {useStore} from "vue2-helpers/vuex";

const route = useRoute();
const store = useStore();
const [tasksListUpdated, setTasksListUpdated] = useTrigger();
const storeTrigger = useStoreTrigger();

const project = ref(null);
const projectId = computed(() => project.value?.id);
const displayType = ref('pipeline');
const tasksCount = ref(null);
const filteredCompaniesCount = ref(null);
const showSideBar = ref(true);
const selectedRows = ref([]);
const selectedAllResults = ref(false);
const addProjectTaskModal = ref(null);
const projectTaskViewModal = ref(null);
const tasksBoard = ref(null);
const filters = ref(null);
const editProject = ref(null);
const serverFilters = ref({});

const filtersApplied = computed(() => {
    return filters.value?.filtersApplied || false;
})

watch(tasksListUpdated, async (value) => {
    if (value) {
        storeTrigger('setNeedUpdateTrackerLogs');

        const {data} = await projectsService.getTasksCount(project.value.id);

        tasksCount.value = data;
    }
});

watch(projectTaskViewModal, (value) => {
    if (value && route.query.view_task) {
        projectTaskViewModal.value.open(route.query.view_task);
    }
});

onMounted(() => {
    getInfo();
    store.dispatch('fetchTasks');
});

async function getInfo() {
    const {data} = await projectsService.getInfo(route.params.id);

    project.value = data;
    tasksCount.value = data.tasks_count;

    document.title = project.value.name + ' | SwitchPitch';
}

function addTask() {
    addProjectTaskModal.value.open();
}

function showTask(taskId) {
    projectTaskViewModal.value.open(taskId);
}

function commentAdded() {
    tasksBoard.value?.updateTask();
}

provide('tracker', project);
provide('projectId', projectId);
provide('companiesCount', tasksCount);
provide('filteredCompaniesCount', filteredCompaniesCount);
provide('serverFilters', serverFilters);
provide('filtersApplied', filtersApplied);
provide('displayType', displayType);
provide('itemNoun', 'task');
provide('isProject', true);
provide('showSideBar', showSideBar);
provide('selectedRows', selectedRows);
provide('selectedAllResults', selectedAllResults);
provide('tasksListUpdated', tasksListUpdated);
provide('setTasksListUpdated', setTasksListUpdated);
provide('addTask', addTask);
provide('showProjectTask', showTask);
</script>

<template>
    <div v-if="project">
        <div id="pipeline" class="tracker-type">
            <TopBar/>

            <div class="tracker-list__actions align-items-end pb-3" :class="{'tracker-list__actions--collapsed': !showSideBar}">
                <div class="page-length pb-2">
                    <template v-if="project.status_data?.value">
                        <h4 class="heading-pending">{{project.status_data.value}}</h4>

                        <ul class="pending-status">
                            <li
                                v-for="(stepName, index) of project.status_data.steps"
                                :key="index"
                                v-b-popover.hover.bottom="stepName"
                                :class="{'pending-status__active': index === (project.status_data.step - 1) }"
                            />
                        </ul>
                    </template>
                </div>

                <div class="dropdown--more">
                    <b-dropdown id="dropdown-1" text="More" class="">
                        <li class="dropdown--more__content">
                            <ul class="actions-list">
                                <li class="actions-list__filter" :class="{'actions-list__filter--active': filters?.filtersApplied}">
                                    <a @click="filters.open()">Filters</a>
                                </li>

                                <li class="actions-list__edit__tracker">
                                    <a @click="editProject.open(project.id, trackerTypes.PROJECT)">Edit Project</a>
                                </li>

                                <li class="actions-list__add">
                                    <a @click="addTask">Add A Task</a>
                                </li>
                            </ul>

                            <Teleport to="body">
                                <EditProject
                                    ref="editProject"
                                    @trackerSaved="getInfo"
                                />
                            </Teleport>
                        </li>
                    </b-dropdown>
                </div>
            </div>

            <div class="tracker-type__scroll-section">
                <SideBar/>

                <div class="tracker-table-slide">
                    <div class="scroll-anchor"></div>

                    <main class="main main--dark main--dark--tracker">
                        <TasksGrid
                            v-if="displayType === 'list'"
                            @showTask="showTask"
                        />

                        <TasksBoard
                            ref="tasksBoard"
                            v-if="displayType === 'pipeline'"
                            @showTask="showTask"
                            @addTask="(statusId) => addProjectTaskModal.open(statusId)"
                        />
                    </main>
                </div>
            </div>
        </div>

        <AddProjectTask
            ref="addProjectTaskModal"
            :projectId="project.id"
            @added="setTasksListUpdated"
        />

        <ProjectTaskView
            ref="projectTaskViewModal"
            @taskUpdated="setTasksListUpdated"
            @commentAdded="commentAdded"
        />

        <Filters ref="filters"/>
    </div>
</template>

<style scoped>
    .popover {
        padding: 6px 10px 4px;
        max-width: 300px;
        width: auto;
    }
</style>
